import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { BRAND_KIT, FONT_WEIGHTS } from "@/constants/brandKitTestIds";
import {
  Field,
  TextInput,
  TextArea,
  NumberInput,
  PillToggleGroup,
  BindingPillGroup,
  MultiSelectChips,
  FormActions,
} from "./Primitives";
import { _Header as Header, _Empty as Empty, _RowActions as RowActions } from "./LogosTab";
import useBindings from "@/lib/useBindings";
import useContentCategories from "@/lib/useContentCategories";

const PATH = "fonts";

const EMPTY = {
  name: "",
  family: "",
  weights: [],
  supports_italic: false,
  role_bindings: [],
  foundry: "",
  license_notes: "",
  priority: 100,
  usable_in_category_slugs: [],
};

export default function FontsTab({ brandId, canManage }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const { bindings, loading: bindingsLoading } = useBindings("font", brandId);
  const { categories } = useContentCategories(brandId);
  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c.slug, label: c.name })),
    [categories],
  );

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/brands/${brandId}/brand-kit/${PATH}`);
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, [brandId]);

  const openNew = () => {
    setForm({ ...EMPTY, priority: (items.length + 1) * 10 });
    setEditing("new"); setError("");
  };
  const openEdit = (it) => {
    setForm({
      name: it.name, family: it.family,
      weights: it.weights || [], supports_italic: !!it.supports_italic,
      role_bindings: it.role_bindings || [],
      foundry: it.foundry || "", license_notes: it.license_notes || "",
      priority: it.priority ?? 100,
      usable_in_category_slugs: it.usable_in_category_slugs || [],
    });
    setEditing(it.font_id); setError("");
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      if (editing === "new") await api.post(`/brands/${brandId}/brand-kit/${PATH}`, form);
      else await api.put(`/brands/${brandId}/brand-kit/${PATH}/${editing}`, form);
      close(); await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save font");
    } finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/brands/${brandId}/brand-kit/${PATH}/${id}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not delete font");
    }
  };

  if (loading) return <p className="text-sm text-[#6B6358]">Loading fonts…</p>;

  return (
    <div>
      <Header
        title="Fonts"
        subtitle="Typefaces and their role bindings. Bindings come from the registry and grow as new layouts are registered. Lower priority wins when multiple fonts share a binding."
        canManage={canManage}
        actionTestId={BRAND_KIT.fontAddButton}
        onAdd={openNew}
        active={editing !== null}
      />

      {editing !== null && (
        <form onSubmit={submit} className="border border-[#1A1F2C]/10 bg-white rounded-lg p-6 mb-6">
          <h3 className="font-serif text-lg mb-4">
            {editing === "new" ? "New font" : "Edit font"}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Display name">
              <TextInput value={form.name} onChange={(v) => setForm({ ...form, name: v })}
                testid={BRAND_KIT.fontNameInput} required placeholder="e.g. Fraunces" />
            </Field>
            <Field label="CSS family name">
              <TextInput value={form.family} onChange={(v) => setForm({ ...form, family: v })}
                testid={BRAND_KIT.fontFamilyInput} required placeholder="e.g. Fraunces" />
            </Field>
            <Field label="Weights" className="md:col-span-2">
              <PillToggleGroup
                options={FONT_WEIGHTS}
                selected={form.weights}
                onChange={(v) => setForm({ ...form, weights: v })}
                testidPrefix={BRAND_KIT.fontWeightTogglePrefix}
              />
            </Field>
            <Field label="Italic">
              <label className="inline-flex items-center gap-2 text-sm text-[#3D3A35]">
                <input
                  data-testid={BRAND_KIT.fontItalicToggle}
                  type="checkbox"
                  checked={form.supports_italic}
                  onChange={(e) => setForm({ ...form, supports_italic: e.target.checked })}
                />
                Supports italic
              </label>
            </Field>
            <Field label="Foundry (optional)">
              <TextInput value={form.foundry} onChange={(v) => setForm({ ...form, foundry: v })}
                testid={BRAND_KIT.fontFoundryInput} placeholder="e.g. Undercase Type" />
            </Field>
            <Field label="Role bindings (where this font is used)" className="md:col-span-2">
              <BindingPillGroup
                bindings={bindings}
                loading={bindingsLoading}
                selected={form.role_bindings}
                onChange={(v) => setForm({ ...form, role_bindings: v })}
                testidPrefix={BRAND_KIT.fontRoleTogglePrefix}
              />
            </Field>
            <Field label="License notes (optional)" className="md:col-span-2">
              <TextArea value={form.license_notes} onChange={(v) => setForm({ ...form, license_notes: v })}
                testid={BRAND_KIT.fontLicenseInput} rows={2}
                placeholder="Print rights, seat count, expiration…" />
            </Field>
          </div>

          <div className="border-t border-[#1A1F2C]/10 mt-6 pt-5">
            <div className="flex items-baseline justify-between mb-1">
              <h4 className="font-serif text-base">AI Assembly disambiguation</h4>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#B5462F]">advanced</span>
            </div>
            <p className="text-xs text-[#6B6358] mb-4 max-w-2xl">
              Used by the AI Assembly Agent when multiple fonts share a binding.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Priority (lower = picked first)">
                <NumberInput
                  value={form.priority}
                  onChange={(v) => setForm({ ...form, priority: v ?? 100 })}
                  testid={BRAND_KIT.fontPriorityInput}
                  step="1"
                  placeholder="100"
                />
              </Field>
              <div />
              <Field label="Restrict to content categories (optional)" className="md:col-span-2">
                <MultiSelectChips
                  options={categoryOptions}
                  selected={form.usable_in_category_slugs}
                  onChange={(v) => setForm({ ...form, usable_in_category_slugs: v })}
                  testidPrefix={BRAND_KIT.fontCategoryPrefix}
                  emptyLabel="No content categories defined for this brand yet."
                />
              </Field>
            </div>
          </div>

          {error && <p className="text-sm text-[#B5462F] mt-3">{error}</p>}
          <FormActions submitting={submitting} onCancel={close}
            submitTestId={BRAND_KIT.fontSubmit} cancelTestId={BRAND_KIT.fontCancel} />
        </form>
      )}

      {items.length === 0 ? (
        <Empty testid={BRAND_KIT.fontsEmpty} label="No fonts yet." />
      ) : (
        <div data-testid={BRAND_KIT.fontsList} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((it) => (
            <div
              key={it.font_id}
              data-testid={`${BRAND_KIT.fontRowPrefix}${it.font_id}`}
              className="border border-[#1A1F2C]/10 bg-white rounded-lg p-5"
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-serif text-2xl leading-tight" style={{ fontFamily: it.family }}>
                  {it.name}
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358]">
                  P {it.priority ?? 100}{it.foundry ? ` · ${it.foundry}` : ""}
                </span>
              </div>
              <div className="font-mono text-[11px] text-[#6B6358] mt-1">{it.family}</div>
              {it.weights?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {it.weights.map((w) => (
                    <span key={w} className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-[#F8F5EE] text-[#3D3A35]">
                      {w}
                    </span>
                  ))}
                  {it.supports_italic && (
                    <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-[#F8F5EE] italic text-[#3D3A35]">
                      Italic
                    </span>
                  )}
                </div>
              )}
              {it.role_bindings?.length > 0 && (
                <div className="text-xs text-[#6B6358] mt-3">
                  Used for: {it.role_bindings.join(", ")}
                </div>
              )}
              {it.usable_in_category_slugs?.length > 0 && (
                <p className="text-[11px] text-[#6B6358] mt-1 italic">
                  Categories: {it.usable_in_category_slugs.join(", ")}
                </p>
              )}
              {it.license_notes && (
                <p className="text-xs text-[#3D3A35] mt-2 line-clamp-2">{it.license_notes}</p>
              )}
              {canManage && (
                <RowActions
                  onEdit={() => openEdit(it)}
                  onDelete={() => remove(it.font_id)}
                  editTestId={`${BRAND_KIT.fontEditPrefix}${it.font_id}`}
                  deleteTestId={`${BRAND_KIT.fontDeletePrefix}${it.font_id}`}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
